import {
    createCol,
    DataGrid,
    DataGridCol,
    DataGridFilter,
    DataGridFilterProps,
    DataGridItemAction,
    DataGridMode,
    DataGridState,
    defaultDataGridFilterState
} from "../DataGrid";
import {
    GetArticleListUsingGETRequest,
    JsonArticle,
    JsonArticleInfo,
    JsonArticleInfoSexEnum,
    JsonArticleType
} from "../../generated-api";
import {ItemsState, useAppDispatch, useAppSelector} from "../../store";
import {selectArticles, selectArticleTypes} from "../../store/selectors";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {fetchArticles, saveArticles} from "../../store/articles";
import {OptionValue} from "../../model/form";
import {Box, Button} from "@mui/material";
import {Edit} from "@mui/icons-material";
import {ButtonGroupField} from "../form/ButtonGroupField";
import {fetchArticleType, fetchArticleTypes, saveArticleType} from "../../store/articleTypes";
import {useAppTranslation} from "../../services/i18n";
import EventArticlesModal from "../../pages/EventArticlesModal";
import EventArticleTypeModal from "../../pages/EventArticleTypeModal";
import {addApiResultMessage, ApiChangeType, getApiResult} from "../../helpers/api";
import {articleSize, shorten} from "../../helpers/format";

interface LocalFilter {
    articleTypeIds: number[]
}

interface EventArticlesGridFilter extends DataGridFilter, GetArticleListUsingGETRequest, LocalFilter {
}

interface EventArticlesListGridState extends DataGridState<JsonArticleInfo, EventArticlesGridFilter> {
}

const renderZeroAsEmpty = (v: number | undefined) => v || '';

/** The century does not help to tell the types apart and the name has to fit on one line. */
const shortYear = (title?: string): string => (title || '').replace(/(^|\s)(19|20)(\d{2})/g, '$1$3');

const cols: DataGridCol<JsonArticleInfo>[] = [
    createCol('Název', 'title', 150, 'Název artiklu', (v) => <span style={{whiteSpace: 'nowrap'}} title={v}>{shortYear(v)}</span>),
    createCol('P', 'sex', 30, 'Pohlaví (U = unisex)'),
    createCol('V', 'size', 35, 'Velikost', (v, item) => {
        return articleSize(item.sex, v, true);
    }),
    createCol('Sklad', 'purchasedCnt', '210C', undefined, undefined, [
        createCol('Nákup', 'purchasedCnt', '70C', 'Celkem nakoupeno', renderZeroAsEmpty),
        createCol('Vydáno', 'recCnt', '70C', 'Celkem vydáno', renderZeroAsEmpty),
        createCol('Zbývá', 'recRemainCnt', '70C', 'Zbývá na skladě', renderZeroAsEmpty),
    ]),
    createCol('Preference', 'prefCnt', '160C', undefined, undefined, [
        createCol('Zadáno', 'prefCnt', '70C', 'Celkem preferováno', renderZeroAsEmpty),
        createCol('K vydání', 'prefRemainCnt', '90C', 'Celkem preferovaných, zatím nevydaných', renderZeroAsEmpty),
    ]),
    {
        title: 'Rozdíl',
        size: 60,
        align: 'center',
        tooltip: 'Rozdíl mezi "Celkem na skladě" a "K vydání", tj. delta poptávky/nabídky',
        col: 'prefRemainCnt',
        renderValue: (v, item) => {
            return renderZeroAsEmpty((item.recRemainCnt || 0) - (item.prefRemainCnt || 0))
        }
    }
];

const defaultState: EventArticlesListGridState = {
    filter: {
        ...defaultDataGridFilterState,
        orderCol: undefined,
        articleTypeIds: []
    },
    filterCallback: (filter, item): boolean => {
        if (filter.articleTypeIds.length > 0 && !(filter.articleTypeIds.indexOf(item.articleTypeId || 0) >= 0)) {
            return false;
        }
        return true;
    }
};

const filterFields = (props: DataGridFilterProps<EventArticlesGridFilter>, articleTypes: JsonArticleInfo[]): JSX.Element => {
    const {formProps} = props;
    const options = articleTypes?.length > 0
        ? articleTypes.map((at) => ({
            // the century does not help to tell the types apart
            label: shorten(at.title?.replace(/(^|\s)(19|20)(\d{2})/g, '$1$3'), 3),
            value: at.articleTypeId
        } as OptionValue))
        : [];

    // the chips have to fit next to the buttons of the box
    const zoom = options.length > 9 ? .7 : (options.length > 6 ? .8 : undefined);

    return <Box sx={zoom ? {zoom} : undefined}>
        <ButtonGroupField name={'articleTypeIds'} label={'Typ artiklu'} onChange={formProps.submitForm} options={options} isMulti={true}/>
    </Box>;
}

const EventArticlesList = (props: { eventId: number, maxHeight?: string }) => {
    const {eventId, maxHeight} = props;
    const dispatch = useAppDispatch();
    const t = useAppTranslation();
    const eventArticles = useAppSelector(selectArticles);
    const eventArticleTypes = useAppSelector(selectArticleTypes);

    const [editArticles, setEditArticles] = useState<JsonArticle[] | undefined>(undefined);
    const [editArticleType, setEditArticleType] = useState<JsonArticleType | undefined>(undefined);
    const eventArticleItems = eventArticles.items;

    const handleFetchArticles = useCallback(() => {
        dispatch(fetchArticles({eventId}));
        dispatch(fetchArticleTypes({eventId}));
    }, [eventId, dispatch]);

    const handleEditArticleType = useCallback((articleTypeId?: number) => {
        if (articleTypeId) {
            dispatch(fetchArticleType(articleTypeId)).then((res) => setEditArticleType(getApiResult(res)));
        } else {
            setEditArticleType({eventId});
        }
    }, [dispatch, eventId]);

    const handleSaveArticleType = useCallback(async (articleType: JsonArticleType) => {
        const res = await dispatch(saveArticleType(articleType));
        const item = getApiResult<JsonArticleType>(res);
        if (item) {
            addApiResultMessage(res, {
                [ApiChangeType.NO_CHANGE]: 'Typ artiklu {{title}} ponechán beze změn',
                [ApiChangeType.UPDATED]: 'Typ artiklu {{title}} úspěšně upraven',
                [ApiChangeType.CREATED]: 'Typ artiklu {{title}} úspěšně vytvořen'
            }, t, dispatch);
            setEditArticleType(undefined);
            handleFetchArticles();
        }
    }, [handleFetchArticles, dispatch, t]);

    const handleCancelArticleType = useCallback(() => {
        setEditArticleType(undefined);
    }, []);

    const actions = useMemo((): DataGridItemAction<JsonArticleInfo>[] => [
        {
            title: t('Upravit typ artiklu'),
            icon: <Edit/>,
            callback: (item) => handleEditArticleType(item.articleTypeId),
        }
    ], [handleEditArticleType, t]);

    const handleEditArticles = useCallback(() => {
        setEditArticles(eventArticleItems); // compatible
    }, [eventArticleItems]);

    const handleSaveArticles = useCallback(async (items: JsonArticle[]) => {
        const res = await dispatch(saveArticles(items));
        const data = getApiResult<JsonArticle[]>(res);
        if (data) {
            addApiResultMessage(res, {
                [ApiChangeType.NO_CHANGE]: 'Sklad ponechán beze změn',
                [ApiChangeType.UPDATED]: 'Sklad úspěšně upraven',
                [ApiChangeType.CREATED]: 'Sklad úspěšně uložen'
            }, t, dispatch);
            setEditArticles(undefined);
            handleFetchArticles();
        }

    }, [handleFetchArticles, dispatch, t])

    const handleCancelArticles = useCallback(() => {
        setEditArticles(undefined);
    }, [])

    useEffect(() => {
        handleFetchArticles();
    }, [handleFetchArticles]);

    const itemsState = {
        loading: eventArticles.loading || eventArticleTypes.loading,
        count: eventArticles.count,
        items: []
    } as ItemsState<JsonArticleInfo, EventArticlesGridFilter>;

    if (!itemsState.loading
        && eventArticleTypes.items
        && eventArticleTypes.items.length > 0) {
        eventArticleTypes.items.forEach((at: JsonArticleType) => {
            if (!at.variants?.sizes) {
                return;
            }
            Object.values(JsonArticleInfoSexEnum).forEach((sex) => {
                at.variants?.sizes?.[sex]?.forEach((size) => {
                    if (!at.variants?.sizes || !at.variants.sizes[sex] || !(at.variants.sizes[sex].indexOf(size) >= 0)) {
                        return;
                    }
                    const it = eventArticles.items?.find((a: JsonArticleInfo) => a.articleTypeId === at.articleTypeId && a.size === size && a.sex === sex);
                    if (it) {
                        itemsState.items.push(it);
                    } else {
                        itemsState.items.push({
                            articleTypeId: at.articleTypeId,
                            title: at.title,
                            sex: sex as JsonArticleInfoSexEnum,
                            size: size
                        });
                    }
                })
            })
        });
    }

    return <>
        <div className={'info-box-actions'}>
            <Button variant={'contained'} size={'small'} color={'primary'}
                title={t('Nový typ artiklu')}
                onClick={() => handleEditArticleType()}>{t('Nový')}</Button>
            <Button variant={'contained'} size={'small'} color={'primary'}
                title={t('Upravit sklad')}
                disabled={!(eventArticleTypes.items && eventArticleTypes.items.length > 0)}
                onClick={() => handleEditArticles()}>{t('Sklad')}</Button>
        </div>
        <DataGrid
            cols={cols}
            defaultState={defaultState}
            itemsState={itemsState}
            mode={DataGridMode.CLIENT}
            filterFields={(props) => filterFields(props, eventArticleTypes.items)}
            actions={actions}
            tableProps={{maxHeight}}
        />
        {editArticles && <EventArticlesModal
			item={editArticles}
			onCancel={handleCancelArticles}
			onSave={handleSaveArticles}
		/>}
        {editArticleType && <EventArticleTypeModal
			item={editArticleType}
			onCancel={handleCancelArticleType}
			onSave={handleSaveArticleType}
		/>}
    </>
}

export default EventArticlesList;
